export default `
# Returns and shipment status (Inbox service)

The Inbox service exposes shipment feedback returns: state changes, proofs of delivery, certificates, generated documents and stamped copies. These returns are usually polled periodically, then marked as read after your system has processed them.

Use the PRE environment for every test: \`https://pre-aws.postamail.es/api\`.

## 1. Count unread returns {#1-count-unread-returns}

\`\`\`endpoint
GET /api/inbox/feedback/count
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/inbox/feedback/count\` |
| Operation | \`GET\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`NULL\` |

Use this endpoint for periodic polling. If the result is \`0\`, there is no need to request the detailed list.

### Query parameters

| Name | Type | Description |
|------|------|-------------|
| \`includeread\` | boolean | \`false\` for the usual unread-only count; \`true\` to count already-read returns too. |
| \`includestates\` | boolean | Include state-change returns. |
| \`includeproofs\` | boolean | Include proof-of-delivery returns. |
| \`includecerts\` | boolean | Include certificate returns. |
| \`includedocuments\` | boolean | Include generated shipment document returns. |
| \`includestampedcopies\` | boolean | Include stamped-copy returns, available for Carta Administrativa products. |
| \`includereports\` | boolean | Include report availability returns. |

\`\`\`tabs-returns-count
===curl
curl -X GET "https://pre-aws.postamail.es/api/inbox/feedback/count?includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=true&includestampedcopies=true&includereports=false" \\
  -H "Authorization: Bearer <jwtToken>"
===node
const countUrl = 'https://pre-aws.postamail.es/api/inbox/feedback/count?' + new URLSearchParams({
  includeread: 'false', includestates: 'true', includeproofs: 'true', includecerts: 'true',
  includedocuments: 'true', includestampedcopies: 'true', includereports: 'false'
})
const response = await fetch(countUrl, { headers: { Authorization: 'Bearer ' + jwtToken } })
const totalReturns = await response.json()
===python
response = requests.get(
  'https://pre-aws.postamail.es/api/inbox/feedback/count',
  headers={'Authorization': 'Bearer ' + jwt_token},
  params={
    'includeread': 'false', 'includestates': 'true', 'includeproofs': 'true',
    'includecerts': 'true', 'includedocuments': 'true', 'includestampedcopies': 'true',
    'includereports': 'false'
  }
)
total_returns = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$query = http_build_query(array(
  'includeread' => 'false', 'includestates' => 'true', 'includeproofs' => 'true',
  'includecerts' => 'true', 'includedocuments' => 'true', 'includestampedcopies' => 'true',
  'includereports' => 'false'
));
$curl = curl_init('https://pre-aws.postamail.es/api/inbox/feedback/count?' . $query);
curl_setopt_array($curl, array(CURLOPT_RETURNTRANSFER => true, CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)));
$totalReturns = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
var url = "https://pre-aws.postamail.es/api/inbox/feedback/count?includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=true&includestampedcopies=true&includereports=false";
using var request = new HttpRequestMessage(HttpMethod.Get, url);
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var totalReturns = await response.Content.ReadFromJsonAsync<int>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/inbox/feedback/count?includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=true&includestampedcopies=true&includereports=false"))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## 2. List return summaries {#2-list-return-summaries}

\`\`\`endpoint
GET /api/inbox/feedback
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/inbox/feedback\` |
| Operation | \`GET\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`NULL\` |

This endpoint returns a list of [FeedbackReturn](/docs/api/return-objects) objects ordered by registration time, most recent first. Each item contains \`returnId\`, \`returnType\`, \`shipmentCode\`, \`recordedTime\` and \`read\`.

### Query parameters

| Name | Type | Description |
|------|------|-------------|
| \`pagesize\` | integer | Maximum number of returns to retrieve, e.g. \`100\`. |
| \`page\` | integer | Zero-based page index. |
| \`includeread\` | boolean | Whether already-read returns should be included. |
| \`includestates\` | boolean | Include \`STATE\` returns. |
| \`includeproofs\` | boolean | Include \`PROOF\` returns. |
| \`includecerts\` | boolean | Include \`CERT\` returns. |
| \`includedocuments\` | boolean | Include \`DOCUMENT\` returns. |
| \`includestampedcopies\` | boolean | Include \`STAMPED_COPY\` returns. |
| \`includereports\` | boolean | Include \`REPORT\` returns. |

\`\`\`tabs-returns-list
===curl
curl -X GET "https://pre-aws.postamail.es/api/inbox/feedback?pagesize=100&page=0&includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=false&includestampedcopies=false&includereports=false" \\
  -H "Authorization: Bearer <jwtToken>"
===node
const feedbackUrl = 'https://pre-aws.postamail.es/api/inbox/feedback?pagesize=100&page=0&includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=false&includestampedcopies=false&includereports=false'
const response = await fetch(feedbackUrl, { headers: { Authorization: 'Bearer ' + jwtToken } })
const returns = await response.json()
===python
response = requests.get(
  'https://pre-aws.postamail.es/api/inbox/feedback?pagesize=100&page=0&includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=false&includestampedcopies=false&includereports=false',
  headers={'Authorization': 'Bearer ' + jwt_token}
)
returns = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$curl = curl_init('https://pre-aws.postamail.es/api/inbox/feedback?pagesize=100&page=0&includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=false&includestampedcopies=false&includereports=false');
curl_setopt_array($curl, array(CURLOPT_RETURNTRANSFER => true, CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)));
$returns = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Get, "https://pre-aws.postamail.es/api/inbox/feedback?pagesize=100&page=0&includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=false&includestampedcopies=false&includereports=false");
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var returns = await response.Content.ReadFromJsonAsync<FeedbackReturn[]>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/inbox/feedback?pagesize=100&page=0&includeread=false&includestates=true&includeproofs=true&includecerts=true&includedocuments=false&includestampedcopies=false&includereports=false"))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## 3. Query state details {#3-query-state-details}

\`\`\`endpoint
GET /api/inbox/feedback/states
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/inbox/feedback/states\` |
| Operation | \`GET\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`NULL\` |

Use the \`returnId\` values from \`STATE\` returns to obtain [StateInfo](/docs/api/return-objects) details.

| Query parameter | Value |
|-----------------|-------|
| \`returnids\` | JSON array encoded in the query string, e.g. \`["returnId1","returnId2"]\`. |

\`\`\`tabs-returns-states
===curl
curl -G "https://pre-aws.postamail.es/api/inbox/feedback/states" \\
  --data-urlencode 'returnids=["returnId1","returnId2"]' \\
  -H "Authorization: Bearer <jwtToken>"
===node
const response = await fetch('https://pre-aws.postamail.es/api/inbox/feedback/states?returnids=' + encodeURIComponent(JSON.stringify(returnIds)), {
  headers: { Authorization: 'Bearer ' + jwtToken }
})
const states = await response.json()
===python
response = requests.get(
  'https://pre-aws.postamail.es/api/inbox/feedback/states',
  headers={'Authorization': 'Bearer ' + jwt_token},
  params={'returnids': json.dumps(return_ids)}
)
states = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$returnIds = array('returnId1', 'returnId2');
$curl = curl_init('https://pre-aws.postamail.es/api/inbox/feedback/states?returnids=' . urlencode(json_encode($returnIds)));
curl_setopt_array($curl, array(CURLOPT_RETURNTRANSFER => true, CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)));
$states = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
var ids = Uri.EscapeDataString(JsonSerializer.Serialize(returnIds));
using var request = new HttpRequestMessage(HttpMethod.Get, "https://pre-aws.postamail.es/api/inbox/feedback/states?returnids=" + ids);
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var states = await response.Content.ReadFromJsonAsync<StateInfo[]>();
===java
var ids = URLEncoder.encode('["returnId1","returnId2"]', StandardCharsets.UTF_8);
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/inbox/feedback/states?returnids=" + ids))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## 4. Mark returns as read {#4-mark-returns-as-read}

\`\`\`endpoint
PUT /api/inbox/feedback/read
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/inbox/feedback/read\` |
| Operation | \`PUT\` |
| Header | \`Authorization: Bearer <jwtToken>\`; \`Content-Type: application/json\` |
| Body | JSON array of \`returnId\` strings |

After processing a batch of returns, mark them as read so they are not returned again by unread polling. The response is the number of return IDs marked as read; invalid IDs are ignored and not included in the count.

\`\`\`json
["returnId1", "returnId2", "returnId3"]
\`\`\`

\`\`\`tabs-returns-read
===curl
curl -X PUT "https://pre-aws.postamail.es/api/inbox/feedback/read" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -H "Content-Type: application/json" \\
  -d '["returnId1","returnId2","returnId3"]'
===node
const response = await fetch('https://pre-aws.postamail.es/api/inbox/feedback/read', {
  method: 'PUT',
  headers: { Authorization: 'Bearer ' + jwtToken, 'Content-Type': 'application/json' },
  body: JSON.stringify(returnIds)
})
const markedRead = await response.json()
===python
response = requests.put(
  'https://pre-aws.postamail.es/api/inbox/feedback/read',
  headers={'Authorization': 'Bearer ' + jwt_token},
  json=return_ids
)
marked_read = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$returnIds = array('returnId1', 'returnId2', 'returnId3');
$curl = curl_init('https://pre-aws.postamail.es/api/inbox/feedback/read');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => 'PUT',
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken, 'Content-Type: application/json'),
  CURLOPT_POSTFIELDS => json_encode($returnIds)
));
$markedRead = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Put, "https://pre-aws.postamail.es/api/inbox/feedback/read")
{
  Content = JsonContent.Create(returnIds)
};
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var markedRead = await response.Content.ReadFromJsonAsync<int>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/inbox/feedback/read"))
  .header("Authorization", "Bearer " + jwtToken)
  .header("Content-Type", "application/json")
  .PUT(HttpRequest.BodyPublishers.ofString('["returnId1","returnId2","returnId3"]'))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## 5. Download proof, certificate or document files {#5-download-proof-certificate-or-document-files}

\`\`\`endpoint
GET /api/inbox/feedbackfile/{returnId}
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/inbox/feedbackfile/{returnId}\` |
| Operation | \`GET\` |
| Header | \`Authorization: Bearer <jwtToken>\` |
| Body | \`NULL\` |

Returns of type \`PROOF\`, \`CERT\`, \`DOCUMENT\` and \`STAMPED_COPY\` may have a downloadable PDF or file. Use the \`returnId\` to download the binary content. The file name can be obtained from the standard HTTP response headers.

Related detail endpoints:

| Return type | Detail endpoint |
|-------------|-----------------|
| \`PROOF\` | \`GET /api/inbox/feedback/proofs?returnids=[...]\` |
| \`CERT\` | \`GET /api/inbox/feedback/certs?returnids=[...]\` |
| \`DOCUMENT\` | \`GET /api/inbox/feedback/documents?returnids=[...]\` |
| \`STAMPED_COPY\` | \`GET /api/inbox/feedback/stampedcopy?returnids=[...]\` |

\`\`\`tabs-returns-download
===curl
curl -X GET "https://pre-aws.postamail.es/api/inbox/feedbackfile/<returnId>" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -o feedback.pdf
===node
const response = await fetch('https://pre-aws.postamail.es/api/inbox/feedbackfile/' + returnId, {
  headers: { Authorization: 'Bearer ' + jwtToken }
})
const fileBytes = await response.arrayBuffer()
===python
response = requests.get(
  'https://pre-aws.postamail.es/api/inbox/feedbackfile/' + return_id,
  headers={'Authorization': 'Bearer ' + jwt_token}
)
open('feedback.pdf', 'wb').write(response.content)
===php
<?php
$jwtToken = '<jwtToken>';
$returnId = '<returnId>';
$curl = curl_init('https://pre-aws.postamail.es/api/inbox/feedbackfile/' . $returnId);
curl_setopt_array($curl, array(CURLOPT_RETURNTRANSFER => true, CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)));
$fileBytes = curl_exec($curl);
curl_close($curl);
file_put_contents('feedback.pdf', $fileBytes);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Get, "https://pre-aws.postamail.es/api/inbox/feedbackfile/" + returnId);
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var fileBytes = await response.Content.ReadAsByteArrayAsync();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/inbox/feedbackfile/" + returnId))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofByteArray());
\`\`\`

## 6. Normalized shipment states

| State | Code | Description |
|-------|------|-------------|
| Pre-admitted | \`PRE\` | Carrier has accepted pre-admission. |
| Printed | \`PRI\` | The workshop printed the shipment and marked it as printed. |
| Sent to postal operator | \`SH\` | The shipment left the facility for deposit with the postal operator. |
| Received by postal operator | \`RPO\` | The postal operator received the shipment. |
| Not received | \`NRC\` | The shipment was not received by the postal operator. |
| In transit | \`WAY\` | The shipment is moving through the postal network. |
| Out for delivery | \`OND\` | The shipment is in delivery at the closest office/unit. |
| Delivered at destination | \`DLD\` | Delivered at the destination address. |
| Delivered at office | \`DLO\` | Delivered at the postal office after delivery notice. |
| Delivered | \`DEL\` | Delivered without additional modality details. |
| Expired | \`EXP\` | Returned because it was not collected before expiry. |
| Rejected | \`RJT\` | Rejected by the recipient. |
| Rejected by postal operator | \`REJ\` | Rejected by the postal operator, for example due to invalid postal code. |
| Unknown recipient | \`UNK\` | Delivery attempted, but the recipient does not reside at the destination address. |
| Wrong address | \`WAD\` | Could not be delivered because the address could not be found. |
| Stolen | \`STL\` | The shipment was stolen. |
| Lost | \`LST\` | The shipment is missing. |
| Returned | \`RET\` | The shipment was returned. The postal operator can include a return reason. |
| Deceased | \`DIE\` | Returned after confirming the recipient is deceased. |
| No ID information | \`NIF\` | No information about the shipment. |
| Out of scope | \`OUT\` | Outside delivery scope; no further status is expected from the postal operator. |
| Cancelled | \`CAN\` | The shipment was cancelled. |
| Other | \`OTH\` | Any other undefined state. |
| Electronic proof of delivery | \`EPD\` | Electronic proof certifying delivery by the postal operator was received. |
| Electronic proof of return | \`EPR\` | Electronic proof certifying non-delivery/return was received. |
`



// WEBPACK FOOTER //
// ./src/components/docs/api/content/returns.js